import React, { useState, useRef, useEffect } from 'react'

/**
 * AbwaabPlus Select — trigger (value + chevron) → popover list. Selected option
 * shows a leading check. 36px trigger, 10px radius. RTL-aware.
 *
 * Presentational only — wraps a native value/onChange contract so existing
 * controlled usages map directly.
 */
export type AbwSelectOption = string | { value: string; label: React.ReactNode }

export interface AbwSelectProps {
	value?: string
	onChange?: (value: string) => void
	options?: AbwSelectOption[]
	placeholder?: string
	disabled?: boolean
	style?: React.CSSProperties
}

export function Select({ value, onChange, options = [], placeholder = 'اختر', disabled = false, style }: AbwSelectProps) {
	const [open, setOpen] = useState(false)
	const ref = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const h = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
		}
		document.addEventListener('mousedown', h)
		return () => document.removeEventListener('mousedown', h)
	}, [])
	const norm = options.map(o => (typeof o === 'string' ? { value: o, label: o } : o))
	const selected = norm.find(o => o.value === value)

	return (
		<div ref={ref} style={{ position: 'relative', ...style }}>
			<button
				type="button"
				disabled={disabled}
				onClick={() => setOpen(o => !o)}
				style={{
					width: '100%',
					height: 36,
					padding: '0 12px',
					border: `1px solid ${open ? 'var(--blue)' : 'var(--border)'}`,
					borderRadius: 'var(--r-md)',
					background: 'var(--surface-card)',
					font: 'var(--text-body)',
					color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 8,
					cursor: disabled ? 'not-allowed' : 'pointer',
					opacity: disabled ? 0.5 : 1,
					boxShadow: open ? '0 0 0 3px rgba(6,85,203,0.5)' : 'none',
					transition: 'border-color var(--dur-fast) var(--ease)',
				}}
			>
				{selected ? selected.label : placeholder}
				<span style={{ color: 'var(--text-secondary)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform var(--dur-base) var(--ease)' }}>▾</span>
			</button>
			{open && (
				<div
					style={{
						position: 'absolute',
						insetInlineStart: 0,
						top: 'calc(100% + 4px)',
						zIndex: 30,
						background: 'var(--surface-card)',
						border: '1px solid var(--border)',
						borderRadius: 'var(--r-md)',
						boxShadow: 'var(--sh-hover)',
						padding: 4,
						maxHeight: 240,
						overflowY: 'auto',
						minWidth: '100%',
						width: 'max-content',
						maxWidth: 320,
					}}
				>
					{norm.map(o => {
						const on = o.value === value
						return (
							<div
								key={o.value}
								onClick={() => {
									onChange?.(o.value)
									setOpen(false)
								}}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 8,
									padding: '8px 10px',
									borderRadius: 'var(--r-sm)',
									cursor: 'pointer',
									whiteSpace: 'nowrap',
									font: `var(--fw-${on ? 'medium' : 'regular'}) 14px var(--font-sans)`,
									color: on ? 'var(--blue)' : 'var(--text-primary)',
									background: on ? 'var(--blue-10)' : 'transparent',
								}}
								onMouseEnter={e => {
									if (!on) e.currentTarget.style.background = 'var(--surface-page)'
								}}
								onMouseLeave={e => {
									if (!on) e.currentTarget.style.background = 'transparent'
								}}
							>
								<span style={{ width: 14, color: 'var(--blue)' }}>{on ? '✓' : ''}</span>
								{o.label}
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}
